import { useRef } from 'react'
import Link from 'next/link'
import type { EducatorCohortDetailsResponse } from '@mc/api'
import { SubjectStickyFooter } from '@mc/educator-design-system'
import { useTranslation } from '@mc/i18n'
import { useIsVisible } from '@mc/visibility'
import { EnrollmentBanners } from '../../components/EnrollmentBanners'
import { FooterSection } from '../../components/FooterSection'
import { TopNavigation } from '../../components/TopNavigation'
import { useLandingEnrollment } from '../../hooks/useLandingEnrollment'
import {
  GOLD_STANDARD_INTEGRATIONS,
  TrackClick,
  TrackOnMount,
  TrackPageViewed,
  TrackingAttributes,
  educatorPageNames,
  nameToTrackingAction,
} from '../../tracking/educatorTracking'
import type { SubjectLandingData } from '../../types/subjects'
import { NavBar } from '../EnrollmentPage/components/NavBar'
import {
  SubjectAdmissionsSection,
  SubjectDetailBar,
  SubjectEmailCaptureSection,
  SubjectFaqSection,
  SubjectHeroSection,
  SubjectInstructorsSection,
  SubjectModulesSection,
  SubjectOutcomesSection,
  SubjectProgramSection,
} from './sections'
import { SubjectPageNav } from './sections/SubjectPageNav/SubjectPageNav'

interface SubjectLandingPageProps {
  slug: string
  subjectData: SubjectLandingData
  subjects: { slug: string; name: string; comingSoon?: boolean }[]
  /** SSR-prefetched cohort-details to seed the hero strip + CTAs (no flash). */
  initialCohortDetails?: EducatorCohortDetailsResponse
}

const SUBJECT_PAGE_NAMES = {
  ai_for_leaders: educatorPageNames.SUBJECT_AI_FOR_LEADERS,
  product_innovation: educatorPageNames.SUBJECT_PRODUCT_INNOVATION,
  finance_and_data_intelligence:
    educatorPageNames.SUBJECT_FINANCE_DATA_INTELLIGENCE,
} as const

export const SubjectLandingPage = ({
  slug,
  subjectData,
  subjects,
  initialCohortDetails,
}: SubjectLandingPageProps) => {
  const { t } = useTranslation('@mc/educator')

  const { isVisible: isMobileCtaVisible, ref: mobileCtaRef } =
    useIsVisible<HTMLDivElement>()
  const { isVisible: isDesktopCtaVisible, ref: desktopCtaRef } =
    useIsVisible<HTMLDivElement>()

  // Guard: don't show sticky until the respective CTA has been seen at least once,
  // to avoid a flash before the IntersectionObserver fires on mount.
  const mobileCtaWasVisible = useRef(false)
  const desktopCtaWasVisible = useRef(false)
  if (isMobileCtaVisible) mobileCtaWasVisible.current = true
  if (isDesktopCtaVisible) desktopCtaWasVisible.current = true

  const isStickyVisible =
    (mobileCtaWasVisible.current && !isMobileCtaVisible) ||
    (desktopCtaWasVisible.current && !isDesktopCtaVisible)

  const analyticsSlug = nameToTrackingAction(subjectData.hero.categoryLabel)
  const pageName =
    SUBJECT_PAGE_NAMES[analyticsSlug as keyof typeof SUBJECT_PAGE_NAMES] ??
    educatorPageNames.SUBJECT_LANDING

  const { banner, heroStrip, primaryCta, secondaryCta, isPrimaryLogIn } =
    useLandingEnrollment(initialCohortDetails)

  const heroCtaLabel = primaryCta.label
  const heroCtaHref = primaryCta.href
  const admissionsCtaHref = primaryCta.href
  const stickyCtaLabel = heroCtaLabel.toLowerCase()

  return (
    <TrackingAttributes page={pageName} page_source={pageName}>
      <TrackPageViewed
        name={pageName}
        options={{ integrations: GOLD_STANDARD_INTEGRATIONS }}
      />
      <TrackOnMount event='educatorSignViewed' name={pageName} />
      <TrackOnMount
        event='educatorProductViewed'
        product_name='educator'
        content_type={['product']}
        content_ids={['educator']}
        content_name={['Educator']}
      />
      <TopNavigation hideMobileMenu pageSource={pageName} />
      <NavBar hideDesktopNav />
      <SubjectPageNav
        subjects={subjects}
        currentSlug={slug}
        ctaHref={admissionsCtaHref}
        ctaLabel={heroCtaLabel}
        secondaryCtaLabel={secondaryCta.label}
        secondaryCtaHref={secondaryCta.href}
        isPrimaryLogIn={isPrimaryLogIn}
      />
      <div className='tailwind-enabled bg-black min-h-screen pt-[66px] md:pt-[120px] pb-[68px] md:pb-0'>
        <EnrollmentBanners banner={banner} withTopOffset={false} />
        <SubjectHeroSection
          data={subjectData.hero}
          ctaLabel={heroCtaLabel}
          ctaHref={heroCtaHref}
          heroStrip={heroStrip}
          mobileCtaButtonRef={mobileCtaRef}
          desktopCtaButtonRef={desktopCtaRef}
        />
        <SubjectDetailBar
          details={subjectData.hero.details}
          partnerLogoAlt={subjectData.hero.partnerLogoAlt}
          heroStrip={heroStrip}
        />
        {subjectData.outcomes && subjectData.outcomes.length > 0 && (
          <SubjectOutcomesSection
            sectionTitle={t('subjectLandingPage.subjectOutcomes.sectionTitle')}
            outcomes={subjectData.outcomes}
          />
        )}
        <SubjectModulesSection
          sectionTitle={t('subjectLandingPage.subjectModules.sectionTitle')}
          modules={subjectData.modules}
        />
        {subjectData.instructors && subjectData.instructors.length > 0 && (
          <SubjectInstructorsSection
            sectionTitle={t(
              'subjectLandingPage.subjectInstructors.sectionTitle',
            )}
            instructors={subjectData.instructors}
          />
        )}
        <div className='flex flex-col'>
          <div className='order-2 md:order-1'>
            <SubjectProgramSection
              sectionTitle={t('subjectLandingPage.subjectProgram.sectionTitle')}
              subjects={(subjectData.programSubjects ?? []).filter(
                (s) =>
                  s.pageSlug !== slug &&
                  subjects.some((sub) => sub.slug === s.pageSlug),
              )}
            />
          </div>
          <div className='order-1 md:order-2'>
            <SubjectAdmissionsSection
              ctaHref={admissionsCtaHref}
              ctaLabel={primaryCta.label}
              isPrimaryLogIn={isPrimaryLogIn}
            />
          </div>
        </div>
        <SubjectEmailCaptureSection />
        <SubjectFaqSection />
        <FooterSection linkLocation='footer' margin='prePaywall' />
      </div>
      <SubjectStickyFooter
        isVisible={isStickyVisible}
        text={t('subjectLandingPage.stickyFooter.text')}
        renderCta={(className) => (
          <TrackClick
            event='educatorSignClick'
            action={stickyCtaLabel}
            location='sticky footer'
          >
            {isPrimaryLogIn ? (
              <Link href={admissionsCtaHref} className={className}>
                {heroCtaLabel}
              </Link>
            ) : (
              <TrackClick
                event='educatorPurchaseClick'
                cta_location='sticky footer'
                cta={stickyCtaLabel}
              >
                <Link href={admissionsCtaHref} className={className}>
                  {heroCtaLabel}
                </Link>
              </TrackClick>
            )}
          </TrackClick>
        )}
      />
    </TrackingAttributes>
  )
}
